import React, { useEffect, useRef, useState } from "react";
import { X, Scissors, Play, Pause, Loader2, Crop } from "lucide-react";

const TARGET_AR = 9 / 16;  // output is 9:16 vertical
const MAX_TRIM_S = 20;

/**
 * VideoTrimModal lets the user pick a [start, end] sub-window (≤ 20s) of a
 * video they just selected, AND drag a 9:16 crop frame across the source
 * video to control which part stays inside the final scene.
 *
 * Props:
 *   - file: File (the picked video)
 *   - onCancel: () => void
 *   - onConfirm: ({ start_ms, end_ms, crop: {x,y,w,h} | null }) => void
 *   - confirming: boolean (parent is uploading; disables the confirm button)
 */
export default function VideoTrimModal({ file, onCancel, onConfirm, confirming }) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [src, setSrc] = useState("");
  const [vw, setVw] = useState(0);
  const [vh, setVh] = useState(0);
  const [startMs, setStartMs] = useState(0);
  const [endMs, setEndMs] = useState(MAX_TRIM_S * 1000);
  const [playing, setPlaying] = useState(false);
  // cropOffsetPct = 0..1, position of crop window's TOP-LEFT corner along the
  // free axis (x for landscape sources, y for tall sources). Centered by default.
  const [cropOffsetPct, setCropOffsetPct] = useState(0.5);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onLoaded = () => {
    const v = videoRef.current;
    if (!v) return;
    const dur = v.duration;
    setDuration(dur);
    setVw(v.videoWidth || 0);
    setVh(v.videoHeight || 0);
    setStartMs(0);
    setEndMs(Math.min(MAX_TRIM_S, dur) * 1000);
  };

  // Auto-pause when current time leaves the [start,end] window
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      const t = v.currentTime * 1000;
      if (playing && t >= endMs) {
        v.pause();
        v.currentTime = startMs / 1000;
        setPlaying(false);
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [playing, startMs, endMs]);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (playing) {
      v.pause();
      setPlaying(false);
    } else {
      if (v.currentTime * 1000 < startMs || v.currentTime * 1000 >= endMs) {
        v.currentTime = startMs / 1000;
      }
      v.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  // Crop geometry (in source pixels). Returned to parent on confirm; null when
  // source is already 9:16 (no crop needed).
  const computeCrop = () => {
    if (!vw || !vh) return null;
    const srcAR = vw / vh;
    const eps = 0.005;
    if (Math.abs(srcAR - TARGET_AR) < eps) return null;  // already 9:16
    if (srcAR > TARGET_AR) {
      // wider than 9:16 → vertical slice; user picks horizontal offset
      const cw = Math.round(vh * TARGET_AR);
      const ch = vh;
      const cx = Math.round(cropOffsetPct * (vw - cw));
      return { x: Math.max(0, cx), y: 0, w: cw, h: ch };
    }
    // taller than 9:16 → horizontal slice; user picks vertical offset
    const cw = vw;
    const ch = Math.round(vw / TARGET_AR);
    const cy = Math.round(cropOffsetPct * (vh - ch));
    return { x: 0, y: Math.max(0, cy), w: cw, h: ch };
  };

  const crop = computeCrop();
  const cropAxis = vw && vh && (vw / vh > TARGET_AR ? "x" : vw / vh < TARGET_AR ? "y" : null);
  // Mask geometry as % of preview frame, mirrors the source pixels above
  const maskStyle = (() => {
    if (!crop || !vw || !vh) return null;
    return {
      leftPct: (crop.x / vw) * 100,
      topPct: (crop.y / vh) * 100,
      wPct: (crop.w / vw) * 100,
      hPct: (crop.h / vh) * 100,
    };
  })();

  const trimDur = (endMs - startMs) / 1000;
  const overLimit = trimDur > MAX_TRIM_S + 0.05;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-sm flex items-center justify-center p-3 overflow-y-auto"
      data-testid="video-trim-modal"
    >
      <div
        className="w-full max-w-5xl border border-zinc-800 bg-zinc-950 rounded-sm p-5 md:p-7 my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <Scissors className="w-4 h-4 text-[#FF4400]" />
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FF4400]">
                · Ritaglia il video
              </div>
            </div>
            <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter text-white">
              Scegli i {MAX_TRIM_S}s migliori e l'inquadratura 9:16
            </h2>
          </div>
          <button
            onClick={() => !confirming && onCancel()}
            data-testid="video-trim-cancel"
            className="w-8 h-8 rounded-sm flex items-center justify-center text-zinc-500 hover:text-white hover:bg-zinc-900"
            disabled={confirming}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* LEFT: Video preview + draggable crop overlay */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Anteprima sorgente
            </div>
            <div className="relative inline-block w-full">
              <video
                ref={videoRef}
                src={src}
                onLoadedMetadata={onLoaded}
                playsInline
                className="w-full h-auto max-h-[60vh] rounded-sm bg-black border border-zinc-800"
                controls={false}
              />
              {/* Dark mask + 9:16 window */}
              {maskStyle && cropAxis && (
                <CropOverlay
                  axis={cropAxis}
                  offsetPct={cropOffsetPct}
                  setOffsetPct={setCropOffsetPct}
                  mask={maskStyle}
                />
              )}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <button
                onClick={togglePlay}
                data-testid="video-trim-play"
                className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-800 hover:border-[#FF4400] hover:text-[#FF4400] text-zinc-300 rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors"
              >
                {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                {playing ? "Pausa" : "Anteprima selezione"}
              </button>
              <div className="text-xs text-zinc-500 font-mono">
                {duration ? `${duration.toFixed(1)}s totali` : "..."}
                {vw && vh ? ` · ${vw}×${vh}` : ""}
              </div>
            </div>

            {cropAxis && (
              <div className="mt-3 border border-zinc-800 bg-zinc-900 rounded-sm p-3 flex items-start gap-2">
                <Crop className="w-3.5 h-3.5 text-[#FF4400] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-zinc-400 font-body">
                  Trascina la cornice arancione {cropAxis === "x" ? "in orizzontale" : "in verticale"} sul video per scegliere cosa rimane visibile nel formato 9:16.
                </div>
              </div>
            )}
          </div>

          {/* RIGHT: Trim timeline */}
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
              Selezione tempo (max {MAX_TRIM_S}s)
            </div>
            <DualRangeSlider
              durationMs={duration * 1000}
              startMs={startMs}
              endMs={endMs}
              setStartMs={setStartMs}
              setEndMs={setEndMs}
              maxRangeMs={MAX_TRIM_S * 1000}
              videoRef={videoRef}
            />

            <div className="mt-4 grid grid-cols-3 gap-2">
              <Stat label="Inizio" value={`${(startMs / 1000).toFixed(1)}s`} testId="trim-start-label" />
              <Stat label="Durata" value={`${trimDur.toFixed(1)}s`} highlight testId="trim-duration-label" />
              <Stat label="Fine" value={`${(endMs / 1000).toFixed(1)}s`} testId="trim-end-label" />
            </div>

            {overLimit && (
              <p className="mt-3 text-xs text-[#FF4400] font-mono uppercase tracking-widest" data-testid="trim-over-limit">
                · Riduci la selezione a max {MAX_TRIM_S}s
              </p>
            )}

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => !confirming && onCancel()}
                disabled={confirming}
                data-testid="video-trim-cancel-btn"
                className="px-4 py-3 border border-zinc-800 hover:border-zinc-700 text-zinc-300 hover:text-white rounded-sm font-body transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={() => onConfirm({
                  start_ms: Math.round(startMs),
                  end_ms: Math.round(endMs),
                  crop: crop ? { x: crop.x, y: crop.y, w: crop.w, h: crop.h } : null,
                })}
                disabled={confirming || overLimit || !duration}
                data-testid="video-trim-confirm"
                className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF4400] hover:bg-[#FF6633] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-bold rounded-sm transition-colors"
              >
                {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scissors className="w-4 h-4" />}
                {confirming ? "Elaborazione..." : `Usa questi ${trimDur.toFixed(1)}s`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, testId }) {
  return (
    <div className={`border ${highlight ? "border-[#FF4400] bg-[#FF4400]/5" : "border-zinc-800 bg-zinc-900"} rounded-sm p-3 text-center`}>
      <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div
        className={`font-display font-black text-lg tracking-tighter ${highlight ? "text-[#FF4400]" : "text-white"}`}
        data-testid={testId}
      >
        {value}
      </div>
    </div>
  );
}

/**
 * Draggable 9:16 crop window overlaid on the source video. Constrained to one
 * axis depending on whether the source is wider or taller than 9:16.
 */
function CropOverlay({ axis, offsetPct, setOffsetPct, mask }) {
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const onMove = (clientX, clientY) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (axis === "x") {
      const localX = clientX - rect.left;
      const winW = (mask.wPct / 100) * rect.width;
      const maxOffset = rect.width - winW;
      if (maxOffset <= 0) return;
      const newLeft = Math.max(0, Math.min(maxOffset, localX - winW / 2));
      setOffsetPct(newLeft / maxOffset);
    } else {
      const localY = clientY - rect.top;
      const winH = (mask.hPct / 100) * rect.height;
      const maxOffset = rect.height - winH;
      if (maxOffset <= 0) return;
      const newTop = Math.max(0, Math.min(maxOffset, localY - winH / 2));
      setOffsetPct(newTop / maxOffset);
    }
  };

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onMove(e.clientX, e.clientY);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    onMove(e.clientX, e.clientY);
  };
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div
      ref={containerRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid="video-trim-crop-overlay"
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
      style={{ touchAction: "none" }}
    >
      {/* Dimming mask: 4 dark rectangles around the visible 9:16 window */}
      <div
        className="absolute bg-black/60 pointer-events-none"
        style={{ left: 0, top: 0, width: "100%", height: `${mask.topPct}%` }}
      />
      <div
        className="absolute bg-black/60 pointer-events-none"
        style={{ left: 0, top: `${mask.topPct + mask.hPct}%`, width: "100%", bottom: 0 }}
      />
      <div
        className="absolute bg-black/60 pointer-events-none"
        style={{ left: 0, top: `${mask.topPct}%`, width: `${mask.leftPct}%`, height: `${mask.hPct}%` }}
      />
      <div
        className="absolute bg-black/60 pointer-events-none"
        style={{ left: `${mask.leftPct + mask.wPct}%`, top: `${mask.topPct}%`, right: 0, height: `${mask.hPct}%` }}
      />
      {/* The 9:16 window border */}
      <div
        className="absolute border-2 border-[#FF4400] pointer-events-none"
        style={{
          left: `${mask.leftPct}%`,
          top: `${mask.topPct}%`,
          width: `${mask.wPct}%`,
          height: `${mask.hPct}%`,
        }}
      >
        <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-[#FF4400] text-white font-mono text-[9px] uppercase tracking-widest font-bold whitespace-nowrap">
          9:16
        </div>
      </div>
    </div>
  );
}

/**
 * Two thumbs (start + end) on a single timeline. Constraint: end - start ≤ MAX_TRIM_S
 */
function DualRangeSlider({ durationMs, startMs, endMs, setStartMs, setEndMs, maxRangeMs, videoRef }) {
  const trackRef = useRef(null);
  const dragging = useRef(null); // "start" | "end" | null
  const total = Math.max(1, durationMs);

  const pctOfMs = (ms) => (ms / total) * 100;

  const onMoveX = (clientX) => {
    const el = trackRef.current;
    if (!el || !dragging.current) return;
    const rect = el.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const ms = Math.round(pct * total);
    if (dragging.current === "start") {
      const ns = Math.max(0, Math.min(ms, endMs - 200));
      setStartMs(ns);
      // Constrain end to keep window ≤ MAX
      if (endMs - ns > maxRangeMs) {
        setEndMs(Math.min(total, ns + maxRangeMs));
      }
      if (videoRef?.current) videoRef.current.currentTime = ns / 1000;
    } else {
      const ne = Math.min(total, Math.max(ms, startMs + 200));
      setEndMs(ne);
      if (ne - startMs > maxRangeMs) {
        setStartMs(Math.max(0, ne - maxRangeMs));
      }
      if (videoRef?.current) videoRef.current.currentTime = ne / 1000;
    }
  };

  const onPointerDownStart = (e) => { dragging.current = "start"; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerDownEnd = (e) => { dragging.current = "end"; e.currentTarget.setPointerCapture?.(e.pointerId); };
  const onPointerMove = (e) => onMoveX(e.clientX);
  const onPointerUp = () => { dragging.current = null; };

  return (
    <div className="select-none">
      <div
        ref={trackRef}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="relative h-12 bg-zinc-900 border border-zinc-800 rounded-sm"
        data-testid="trim-track"
        style={{ touchAction: "none" }}
      >
        {/* Selected range fill */}
        <div
          className="absolute top-0 bottom-0 bg-[#FF4400]/25 border-l-2 border-r-2 border-[#FF4400]"
          style={{ left: `${pctOfMs(startMs)}%`, width: `${pctOfMs(endMs - startMs)}%` }}
        />
        {/* Start thumb */}
        <div
          onPointerDown={onPointerDownStart}
          data-testid="trim-thumb-start"
          className="absolute top-0 bottom-0 w-3 bg-[#FF4400] cursor-ew-resize"
          style={{ left: `calc(${pctOfMs(startMs)}% - 6px)`, touchAction: "none" }}
        >
          <div className="absolute inset-y-0 left-1/2 w-px bg-white/70" />
        </div>
        {/* End thumb */}
        <div
          onPointerDown={onPointerDownEnd}
          data-testid="trim-thumb-end"
          className="absolute top-0 bottom-0 w-3 bg-[#FF4400] cursor-ew-resize"
          style={{ left: `calc(${pctOfMs(endMs)}% - 6px)`, touchAction: "none" }}
        >
          <div className="absolute inset-y-0 left-1/2 w-px bg-white/70" />
        </div>
      </div>
      <div className="flex justify-between mt-1 font-mono text-[9px] uppercase tracking-widest text-zinc-600">
        <span>0s</span>
        <span>{(durationMs / 1000).toFixed(1)}s</span>
      </div>
    </div>
  );
}
